/*
 * Duo Gift Arcade - shared reward claim UI (Phase 1B)
 * ---------------------------------------------------------------------------
 * Mounts a reward card as an OVERLAY over a scored game's reveal screen (dimmed
 * backdrop, centered card, close button back to the design + cart). It NEVER
 * hard-gates the result: closing the overlay returns to the door/picks and the
 * add-to-cart button. Flow:
 *   1. on mount, POST /apps/product-options/score {game, score} -> tier + token
 *   2. Finish tier: NO overlay (since 2026-07-07). The reveal underneath
 *      celebrates inline; only code-carrying tiers (bronze+) pop the card.
 *   3. Monetary tier: email + optional consent -> POST /claim {token, email}.
 * Honors the backend `rewardsLive` flag. Gold codes mint + email automatically
 * since 2026-07-19 (rewards@dialupduo.com via the product-options worker);
 * replay-gated % tiers still return status 'pending' and reserve the code.
 * The response's `emailed` flag is true ONLY when the reward email actually
 * sent, and "We emailed it to you too." renders ONLY off that flag, so the
 * copy can never overstate delivery (a send failure shows the code alone).
 *
 * The claim form carries the program's legal disclosures (2026-07-15): a 13+
 * age attestation in the fine print, and an expandable "Reward terms" list
 * (free to play / no purchase necessary, one reward per person, single-use
 * 30-day codes, no cash value, sponsor identity, right to change the program).
 * Keep those disclosures in sync with the worker's REWARD_TTL_SEC and dedupe
 * rules; they are the FTC material terms for the promotion.
 *
 * Exposed as window.DuoArcadeClaim.mount({ game, score, slot }). `slot` is a
 * marker element inside the reveal; the overlay is attached to the reveal stage.
 * No em dashes in any customer-facing copy (owner rule).
 */
(function () {
  'use strict';

  var PROXY = '/apps/product-options';
  var uid = 0;

  var REWARDS = {
    finish: { title: 'You found your gift match!', blurb: 'Your picks are ready below, saved with a share card to send along.', monetary: false },
    bronze: { title: 'a free multi-color upgrade', blurb: 'A free multi-color upgrade on your Duo door sign, worth $6.', monetary: true },
    silver: { title: 'multi-color plus a size bump', blurb: 'A free multi-color upgrade and one size bump, worth about $13.', monetary: true },
    gold: { title: 'free shipping', blurb: 'Free shipping on your order.', monetary: true },
    platinum: { title: '30% off your order', blurb: 'Take 30% off your order. At checkout you keep whichever discount saves you more.', monetary: true },
    diamond: { title: '35% off your order', blurb: 'Take 35% off your order. At checkout you keep whichever discount saves you more.', monetary: true }
  };

  // Finish tier is non-monetary, unlike the universal monetary tiers.
  // NOTE: since 2026-07-07 a finish-tier result renders inline on the reveal
  // (mount() never builds the overlay when needsCode is false), so this copy is
  // a defensive fallback only. Fidget is the lone arcade game since the
  // single-game cutover (fidget-match3-awesome-plan.md Lane A); Drip Studio and
  // Gift Crush copy retired with them.
  var FINISH_COPY = {
    fidget: { title: 'You found your gift match!', blurb: 'Your picks are ready below, saved with a share card to send along.' }
  };

  function backLabel(game) {
    return game === 'fidget' ? 'See my gift matches' : 'Back to my result';
  }

  // Score line shown at the top of the card for scored games.
  function scoreLine(card) {
    return (card && card.__score > 0) ? '<p class="dac-claim__score">Score ' + card.__score + '</p>' : '';
  }

  var ERRORS = {
    invalid_email: 'Please enter a valid email address.',
    invalid_token: 'This reward expired. Play again to earn a fresh one.',
    turnstile_failed: 'We could not verify you are human. Please try again.',
    rewards_disabled: 'Rewards are not available right now. Please try again later.',
    mint_failed: 'Something went wrong creating your code. Please try again.',
    bad_request: 'Something went wrong. Please try again.'
  };

  function h(tag, cls, html) {
    var e = document.createElement(tag);
    if (cls) e.className = cls;
    if (html != null) e.innerHTML = html;
    return e;
  }

  function isEmail(v) {
    var s = String(v || '').trim();
    return s.length <= 254 && /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(s);
  }

  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  // Expiration disclosure for a delivered code. Prefers the exact date from
  // the claim response (`expiresAt`, epoch seconds, mirrors the Shopify
  // discount's endsAt); falls back to the generic 30-day window when absent.
  function expiryText(j) {
    var sec = Number(j && j.expiresAt) || 0;
    if (sec > 0) {
      var d = new Date(sec * 1000);
      if (!isNaN(d.getTime())) {
        return 'Valid through ' + d.toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric' }) + '.';
      }
    }
    return 'Valid for 30 days.';
  }

  // The reward program's material terms, rendered next to the email capture
  // they govern. Values here must track the worker: REWARD_TTL_SEC (30 days),
  // usageLimit 1, UNIQUE(email_hash), and the WELCOME25 non-stacking rule.
  function termsBlock() {
    return '<details class="dac-claim__terms"><summary>Reward terms</summary><ul>' +
      '<li>Free to play. No purchase is necessary to earn or claim a reward.</li>' +
      '<li>One reward per person and per email address.</li>' +
      '<li>Reward codes are single use and expire 30 days after they are issued.</li>' +
      '<li>Percent-off rewards do not combine with other discount codes. At checkout you keep whichever saves you more.</li>' +
      '<li>Rewards have no cash value and cannot be redeemed for cash.</li>' +
      '<li>You must be 13 or older to claim a reward.</li>' +
      '<li>Rewards are offered by DialUpDuo LLC. The program may change or end at any time; codes already issued stay valid until they expire.</li>' +
      '</ul></details>';
  }

  function track(name, data) {
    try { (window.dataLayer = window.dataLayer || []).push(Object.assign({ event: 'duo_claim_' + name }, data || {})); } catch (e) {}
    try { document.dispatchEvent(new CustomEvent('duo:claim:event', { detail: { name: name, data: data || {} } })); } catch (e2) {}
  }

  function postJson(path, body) {
    return fetch(PROXY + path, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body)
    }).then(function (res) {
      return res.json().catch(function () { return {}; }).then(function (json) {
        return { code: res.status, json: json || {} };
      });
    }).catch(function () {
      return { code: 0, json: {} };
    });
  }

  // The render functions swap this inner container, leaving the close button intact.
  function contentOf(card) { return card.querySelector('.dac-claim__content'); }

  function buildOverlay(slot, game, score) {
    // Overlay the reveal stage so the card sits over the (dimmed) game result.
    var stage = slot.closest('.dfa-reveal') || slot.parentNode || document.body;
    if (stage && getComputedStyle(stage).position === 'static') stage.style.position = 'relative';

    var overlay = h('div', 'dac-claim-overlay');
    overlay.setAttribute('role', 'dialog');
    overlay.setAttribute('aria-modal', 'true');
    overlay.setAttribute('aria-label', 'Your reward');

    var card = h('div', 'dac-claim');
    card.__game = game;
    card.__score = score;
    var close = h('button', 'dac-claim__close', '&times;');
    close.type = 'button';
    close.setAttribute('aria-label', 'Close reward');
    var content = h('div', 'dac-claim__content', '');
    card.appendChild(close);
    card.appendChild(content);
    overlay.appendChild(card);
    stage.appendChild(overlay);

    function closeOverlay() {
      document.removeEventListener('keydown', onKey);
      if (overlay.parentNode) overlay.parentNode.removeChild(overlay);
    }
    function onKey(ev) { if (ev.key === 'Escape') closeOverlay(); }
    card.__close = closeOverlay;
    close.addEventListener('click', closeOverlay);
    overlay.addEventListener('click', function (ev) { if (ev.target === overlay) closeOverlay(); });
    document.addEventListener('keydown', onKey);
    try { close.focus(); } catch (e) {}
    return card;
  }

  function mount(config) {
    var slot = config && config.slot;
    if (!slot || slot.__duoClaim) return;
    slot.__duoClaim = true;

    var game = String(config.game || '');
    var score = Number(config.score) || 0;

    // The score always posts (tier decision + funnel telemetry), but the
    // overlay only mounts for tiers that carry a code. A finish-tier round
    // stays on the reveal, where the gifts are the payoff; popping a card over
    // them read as a dead end (owner call 2026-07-07). Nothing pops while we
    // wait either: the reveal underneath is fully usable.
    postJson('/score', { game: game, score: score }).then(function (res) {
      var data = res.json;
      if (!data || !data.ok) return;      // never block the result
      track('reward_shown', { game: game, tier: data.tier, score: score });
      if (!data.needsCode) return;        // finish tier: the reveal already celebrates inline
      var card = buildOverlay(slot, game, score);
      renderTier(card, game, data);
    });
  }

  function renderTier(card, game, data) {
    card.classList.remove('is-loading');
    var copy = REWARDS[data.tier] || REWARDS.finish;
    var content = contentOf(card);

    if (!data.needsCode) {
      var fin = FINISH_COPY[game] || FINISH_COPY.fidget;
      content.innerHTML =
        scoreLine(card) +
        '<div class="dac-claim__badge" data-tier="finish">Finish</div>' +
        '<h3 class="dac-claim__title">' + fin.title + '</h3>' +
        '<p class="dac-claim__blurb">' + fin.blurb + '</p>' +
        '<button type="button" class="dac-claim__btn dac-claim__btn--ghost" data-dac-done>' + backLabel(game) + '</button>';
      var done = content.querySelector('[data-dac-done]');
      if (done) done.addEventListener('click', function () { if (card.__close) card.__close(); });
      return;
    }

    var live = !!data.rewardsLive;
    var id = 'dac-email-' + (++uid);
    content.innerHTML =
      scoreLine(card) +
      '<div class="dac-claim__badge" data-tier="' + escapeHtml(data.tier) + '">' + escapeHtml(data.tierLabel || '') + '</div>' +
      '<h3 class="dac-claim__title">You earned ' + copy.title + '</h3>' +
      '<p class="dac-claim__blurb">' + copy.blurb + '</p>' +
      '<form class="dac-claim__form" novalidate>' +
        '<label class="dac-claim__label" for="' + id + '">Email for your reward</label>' +
        '<input id="' + id + '" class="dac-claim__input" type="email" inputmode="email" autocomplete="email" placeholder="you@example.com" required>' +
        '<label class="dac-claim__consent"><input type="checkbox" class="dac-claim__check"><span>Email me Duo updates and gift ideas. Optional, unsubscribe anytime.</span></label>' +
        '<button type="submit" class="dac-claim__btn">' + (live ? 'Claim reward' : 'Reserve my reward') + '</button>' +
        '<p class="dac-claim__msg" role="alert"></p>' +
        '<p class="dac-claim__fine">We use your email to send this reward and, if you opt in, occasional updates. See our <a href="/policies/privacy-policy" target="_blank" rel="noopener">privacy policy</a>. By claiming you confirm you are 13 or older.</p>' +
        termsBlock() +
      '</form>';
    wireForm(card, game, data);
  }

  function wireForm(card, game, data) {
    var form = card.querySelector('.dac-claim__form');
    var input = card.querySelector('.dac-claim__input');
    var check = card.querySelector('.dac-claim__check');
    var btn = card.querySelector('.dac-claim__btn');
    var msg = card.querySelector('.dac-claim__msg');

    form.addEventListener('submit', function (ev) {
      ev.preventDefault();
      var email = input.value.trim();
      if (!isEmail(email)) { msg.textContent = ERRORS.invalid_email; input.focus(); return; }
      btn.disabled = true;
      card.classList.add('is-loading');
      msg.textContent = '';
      track('reward_claim_submit', { game: game, tier: data.tier });
      postJson('/claim', { token: data.token, email: email, consent: !!check.checked }).then(function (res) {
        card.classList.remove('is-loading');
        btn.disabled = false;
        handleClaimResult(card, game, data, res.json || {});
      });
    });
  }

  function handleClaimResult(card, game, data, j) {
    if (!j.ok) {
      var msg = card.querySelector('.dac-claim__msg');
      if (msg) msg.textContent = ERRORS[j.error] || 'Something went wrong. Please try again.';
      track('reward_claim_error', { game: game, tier: data.tier, error: j.error || 'unknown' });
      return;
    }
    track('reward_claimed', { game: game, tier: j.tier, status: j.status, delivery: j.delivery });
    renderClaimed(card, j);
  }

  function renderClaimed(card, j) {
    var copy = REWARDS[j.tier] || REWARDS.finish;
    var body;

    if (j.delivery === 'code' && j.rewardCode) {
      // "We emailed it to you too." renders only when the backend confirms the
      // send (j.emailed) so the copy never overstates delivery.
      body =
        '<h3 class="dac-claim__title">Reward unlocked</h3>' +
        '<p class="dac-claim__blurb">Here is your code. Copy it and keep it handy.' + (j.emailed ? ' We emailed it to you too.' : '') + '</p>' +
        codeBlock(j.rewardCode) +
        '<p class="dac-claim__fine">Apply it at checkout. Single use. ' + expiryText(j) + '</p>';
    } else if (j.status === 'already_claimed') {
      body =
        '<h3 class="dac-claim__title">Already claimed</h3>' +
        '<p class="dac-claim__blurb">This email already has a reward. It is one reward per person.' + (j.rewardCode && j.emailed ? ' We emailed it to you too.' : '') + '</p>' +
        (j.rewardCode ? codeBlock(j.rewardCode) + '<p class="dac-claim__fine">Apply it at checkout. Single use. ' + expiryText(j) + '</p>' : '');
    } else {
      body =
        '<h3 class="dac-claim__title">Reward reserved</h3>' +
        '<p class="dac-claim__blurb">Your ' + copy.title + ' is reserved. When rewards launch, we will email your code to this address.</p>' +
        '<p class="dac-claim__fine">If the program changes before launch, we will email you an update instead. Codes are single use and valid for 30 days once issued.</p>';
    }

    card.classList.add('dac-claim--done');
    contentOf(card).innerHTML =
      scoreLine(card) +
      '<div class="dac-claim__badge" data-tier="' + escapeHtml(j.tier) + '">' + escapeHtml(j.tierLabel || '') + '</div>' +
      body +
      '<button type="button" class="dac-claim__btn dac-claim__btn--ghost" data-dac-done>' + backLabel(card.__game) + '</button>';

    var copyBtn = card.querySelector('.dac-claim__copy');
    if (copyBtn) {
      copyBtn.addEventListener('click', function () {
        var code = copyBtn.getAttribute('data-code') || '';
        if (navigator.clipboard && navigator.clipboard.writeText) {
          navigator.clipboard.writeText(code).then(function () {
            copyBtn.textContent = 'Copied';
            setTimeout(function () { copyBtn.textContent = 'Copy'; }, 1600);
          }).catch(function () {});
        }
      });
    }
    var done = card.querySelector('[data-dac-done]');
    if (done) done.addEventListener('click', function () { if (card.__close) card.__close(); });
  }

  function codeBlock(code) {
    return '<div class="dac-claim__code"><code>' + escapeHtml(code) + '</code>' +
      '<button type="button" class="dac-claim__copy" data-code="' + escapeHtml(code) + '">Copy</button></div>';
  }

  window.DuoArcadeClaim = { mount: mount };
})();
